import { useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { 
  Settings, 
  Building2, 
  Mail, 
  Bell,
  Shield,
  Palette,
  Globe,
  Save,
  CreditCard,
  Key,
  Users,
  FileText
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Separator } from '../../components/ui/separator';

const AdminSettingsPage = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');

  const [settings, setSettings] = useState({
    // General
    companyName: 'Silver Brick Development',
    companyEmail: 'info@silverbrick.com.mx',
    companyPhone: '+52 55 1234 5678',
    companyAddress: 'Paseo de la Reforma 222, Ciudad de México',
    
    // Notifications
    emailNotifications: true,
    newUserNotification: true,
    newCommitmentNotification: true,
    kycApprovalNotification: true,
    weeklyDigest: false,
    
    // Investment
    minInvestmentDefault: 250000,
    maxInvestmentDefault: 50000000,
    autoApproveCommitments: false,
    requireKYCForInvestment: true,
    
    // Security
    sessionTimeout: 30,
    twoFactorRequired: false,
    ipWhitelist: '',
    
    // Appearance
    primaryColor: '#C0C0C0',
    logoUrl: '/images/logo.svg',
    faviconUrl: '/favicon.ico'
  });

  const handleSave = async () => {
    setSaving(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSaving(false);
    alert('Configuración guardada correctamente');
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-light tracking-tight">Configuración del Sistema</h1>
          <p className="text-muted-foreground mt-1">
            Administra la configuración general de la plataforma
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="gap-2">
          <Save className="h-4 w-4" />
          {saving ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </div>

      {/* Settings Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="general" className="gap-2">
            <Building2 className="h-4 w-4" /> General
          </TabsTrigger>
          <TabsTrigger value="notifications" className="gap-2">
            <Bell className="h-4 w-4" /> Notificaciones
          </TabsTrigger>
          <TabsTrigger value="investment" className="gap-2">
            <CreditCard className="h-4 w-4" /> Inversión
          </TabsTrigger>
          <TabsTrigger value="security" className="gap-2">
            <Shield className="h-4 w-4" /> Seguridad
          </TabsTrigger>
          <TabsTrigger value="appearance" className="gap-2">
            <Palette className="h-4 w-4" /> Apariencia
          </TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general">
          <div className="card-premium p-6">
            <h2 className="text-lg font-medium mb-6 flex items-center gap-2">
              <Building2 className="h-5 w-5" strokeWidth={1.5} />
              Información de la Empresa
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Nombre de la Empresa</Label>
                <Input
                  value={settings.companyName}
                  onChange={(e) => handleChange('companyName', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Email de Contacto</Label>
                <Input
                  type="email"
                  value={settings.companyEmail}
                  onChange={(e) => handleChange('companyEmail', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input
                  value={settings.companyPhone}
                  onChange={(e) => handleChange('companyPhone', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Dirección</Label>
                <Input
                  value={settings.companyAddress}
                  onChange={(e) => handleChange('companyAddress', e.target.value)}
                />
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Notification Settings */}
        <TabsContent value="notifications">
          <div className="card-premium p-6">
            <h2 className="text-lg font-medium mb-6 flex items-center gap-2">
              <Bell className="h-5 w-5" strokeWidth={1.5} />
              Preferencias de Notificaciones
            </h2>
            
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Notificaciones por Email</p>
                  <p className="text-sm text-muted-foreground">Recibir notificaciones por correo electrónico</p>
                </div>
                <Switch
                  checked={settings.emailNotifications}
                  onCheckedChange={(v) => handleChange('emailNotifications', v)}
                />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Nuevo Usuario Registrado</p>
                  <p className="text-sm text-muted-foreground">Notificar cuando un nuevo usuario se registre</p>
                </div>
                <Switch
                  checked={settings.newUserNotification}
                  onCheckedChange={(v) => handleChange('newUserNotification', v)}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Nuevo Compromiso de Inversión</p>
                  <p className="text-sm text-muted-foreground">Notificar cuando se registre un nuevo compromiso</p>
                </div>
                <Switch
                  checked={settings.newCommitmentNotification}
                  onCheckedChange={(v) => handleChange('newCommitmentNotification', v)}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Aprobación KYC</p>
                  <p className="text-sm text-muted-foreground">Notificar cuando un KYC requiera revisión</p>
                </div>
                <Switch
                  checked={settings.kycApprovalNotification}
                  onCheckedChange={(v) => handleChange('kycApprovalNotification', v)}
                />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Resumen Semanal</p>
                  <p className="text-sm text-muted-foreground">Recibir un resumen semanal de actividad</p>
                </div>
                <Switch
                  checked={settings.weeklyDigest}
                  onCheckedChange={(v) => handleChange('weeklyDigest', v)}
                />
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Investment Settings */}
        <TabsContent value="investment">
          <div className="card-premium p-6">
            <h2 className="text-lg font-medium mb-6 flex items-center gap-2">
              <CreditCard className="h-5 w-5" strokeWidth={1.5} />
              Configuración de Inversiones
            </h2>
            
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Inversión Mínima por Defecto (MXN)</Label>
                  <Input
                    type="number"
                    value={settings.minInvestmentDefault}
                    onChange={(e) => handleChange('minInvestmentDefault', parseInt(e.target.value))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Inversión Máxima por Defecto (MXN)</Label>
                  <Input
                    type="number"
                    value={settings.maxInvestmentDefault}
                    onChange={(e) => handleChange('maxInvestmentDefault', parseInt(e.target.value))}
                  />
                </div>
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Auto-aprobar Compromisos</p>
                  <p className="text-sm text-muted-foreground">Aprobar automáticamente compromisos de inversionistas verificados</p>
                </div>
                <Switch
                  checked={settings.autoApproveCommitments}
                  onCheckedChange={(v) => handleChange('autoApproveCommitments', v)}
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Requerir KYC para Invertir</p>
                  <p className="text-sm text-muted-foreground">Los usuarios deben completar el KYC antes de poder invertir</p>
                </div>
                <Switch
                  checked={settings.requireKYCForInvestment}
                  onCheckedChange={(v) => handleChange('requireKYCForInvestment', v)}
                />
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Security Settings */}
        <TabsContent value="security">
          <div className="card-premium p-6">
            <h2 className="text-lg font-medium mb-6 flex items-center gap-2">
              <Shield className="h-5 w-5" strokeWidth={1.5} />
              Configuración de Seguridad
            </h2>
            
            <div className="space-y-6">
              <div className="space-y-2">
                <Label>Tiempo de Sesión (minutos)</Label>
                <Input
                  type="number"
                  value={settings.sessionTimeout}
                  onChange={(e) => handleChange('sessionTimeout', parseInt(e.target.value))}
                  className="max-w-xs"
                />
                <p className="text-xs text-muted-foreground">Tiempo de inactividad antes de cerrar sesión automáticamente</p>
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Autenticación de Dos Factores</p>
                  <p className="text-sm text-muted-foreground">Requerir 2FA para todos los administradores</p>
                </div>
                <Switch
                  checked={settings.twoFactorRequired}
                  onCheckedChange={(v) => handleChange('twoFactorRequired', v)}
                />
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <Label>Lista Blanca de IPs</Label>
                <Textarea
                  value={settings.ipWhitelist}
                  onChange={(e) => handleChange('ipWhitelist', e.target.value)}
                  placeholder="Ingrese IPs separadas por comas (ej: 192.168.1.1, 10.0.0.1)"
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">Dejar vacío para permitir acceso desde cualquier IP</p>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Appearance Settings */}
        <TabsContent value="appearance">
          <div className="card-premium p-6">
            <h2 className="text-lg font-medium mb-6 flex items-center gap-2">
              <Palette className="h-5 w-5" strokeWidth={1.5} />
              Personalización de Apariencia
            </h2>
            
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Color Primario</Label>
                  <div className="flex gap-2">
                    <Input
                      type="color"
                      value={settings.primaryColor}
                      onChange={(e) => handleChange('primaryColor', e.target.value)}
                      className="w-16 h-10 p-1"
                    />
                    <Input
                      value={settings.primaryColor}
                      onChange={(e) => handleChange('primaryColor', e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <Label>URL del Logo</Label>
                <Input
                  value={settings.logoUrl}
                  onChange={(e) => handleChange('logoUrl', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label>URL del Favicon</Label>
                <Input
                  value={settings.faviconUrl}
                  onChange={(e) => handleChange('faviconUrl', e.target.value)}
                />
              </div>
              
              <div className="p-4 bg-muted/30 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  <strong>Nota:</strong> Los cambios de apariencia pueden requerir recargar la página para verse reflejados.
                </p>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminSettingsPage;
